/**
 * @fileoverview deriveCategories — pure translation from ImportRunSummary
 * to the ordered list of accordion descriptors the UI renders.
 * @module import-editor/dryRunSummary
 *
 * Responsibilities:
 * - Build exactly ONE CategoryDescriptor per possible accordion row.
 * - Return descriptors in a stable display order so the UI is predictable.
 *
 * Dependencies:
 * - Pure. Type-only imports.
 *
 * Architecture:
 * - Pure. No DOM, no fetch, no hooks.
 *
 * Notes:
 * - Descriptors with count === 0 are kept (not filtered) and marked
 *   `disabled: true`. The UI is free to hide them — but the Apr 24
 *   design choice is to SHOW zero-count rows so users can verify the
 *   taxonomy at a glance ("Out of Jurisdiction: 0" is a meaningful
 *   diagnostic, not clutter).
 */

/**
 * @process deriveCategories
 *
 * Steps:
 * 1. Read excluded.* / failedByKind.* sub-buckets directly.
 * 2. Emit descriptors in fixed display order.
 *
 * Inputs:
 * - DryRunSummaryView
 *
 * Outputs:
 * - CategoryDescriptor[] in display order.
 *
 * Idempotency:
 * - REQUIRED — pure function of the summary.
 */

import type { CategoryDescriptor, DryRunSummaryView } from './types';

export function deriveCategories(s: DryRunSummaryView): CategoryDescriptor[] {
  const ex = s.excluded;
  const fk = s.failedByKind;

  const created = s.created ?? 0;
  const updated = s.updated ?? 0;
  const removed = s.softDeleted ?? 0;

  const out: CategoryDescriptor[] = [
    {
      key: 'imported.create',
      group: 'imported',
      label: 'Created',
      count: created,
      severity: 'ok',
      description: 'New rows that would be inserted into the database.',
      disabled: created === 0,
    },
    {
      key: 'imported.update',
      group: 'imported',
      label: 'Updated',
      count: updated,
      severity: 'info',
      description: 'Rows that matched an existing record and would be updated.',
      disabled: updated === 0,
    },
    {
      key: 'excluded.outOfJurisdiction',
      group: 'excluded',
      label: 'Out of Jurisdiction',
      count: ex.outOfJurisdiction,
      severity: 'warn',
      description: 'Coordinates fell outside the department boundary polygon.',
      disabled: ex.outOfJurisdiction === 0,
    },
    {
      key: 'excluded.missingCoordinates',
      group: 'excluded',
      label: 'Missing Coordinates',
      count: ex.missingCoordinates,
      severity: 'warn',
      description: 'Jurisdiction filter is on and row had no lat/lon or geometry to check.',
      disabled: ex.missingCoordinates === 0,
    },
    {
      key: 'excluded.duplicateInsertOnly',
      group: 'excluded',
      label: 'Duplicate (Insert-Only)',
      count: ex.duplicateInsertOnly,
      severity: 'warn',
      description: 'Match key already exists and import behavior is insert-only.',
      disabled: ex.duplicateInsertOnly === 0,
    },
    {
      key: 'excluded.missingMatchKey',
      group: 'excluded',
      label: 'Missing Match Key',
      count: ex.missingMatchKey,
      severity: 'warn',
      description: 'Required match key column was blank on this row.',
      disabled: ex.missingMatchKey === 0,
    },
    {
      key: 'excluded.noMatch',
      group: 'excluded',
      label: 'No Match (Update-Only)',
      count: ex.noMatch ?? 0,
      severity: 'warn',
      description: 'Ownership update: no existing parcel matched the row\'s key; update-only imports never create records.',
      disabled: (ex.noMatch ?? 0) === 0,
    },
    {
      key: 'excluded.staleSource',
      group: 'excluded',
      label: 'Stale Source Skipped',
      count: ex.staleSource ?? 0,
      severity: 'warn',
      description: 'The parcel\'s recorded ownership date is already as new or newer than this file — the stale update was refused.',
      disabled: (ex.staleSource ?? 0) === 0,
    },
    {
      key: 'failed.transform',
      group: 'failed',
      label: 'Transform Failed',
      count: fk.transform,
      severity: 'danger',
      description: 'A field transform threw while converting the source cell.',
      disabled: fk.transform === 0,
    },
    {
      key: 'failed.entityBuild',
      group: 'failed',
      label: 'Entity Build Failed',
      count: fk.entityBuild,
      severity: 'danger',
      description: 'The row passed transforms but could not be assembled into a record.',
      disabled: fk.entityBuild === 0,
    },
    {
      key: 'removed',
      group: 'removed',
      label: 'Soft-deleted (Full-Sync)',
      count: removed,
      severity: 'info',
      description: 'Existing records missing from this file that full-sync would soft-delete.',
      disabled: removed === 0,
    },
  ];

  return out;
}

/**
 * Group descriptors by their `group` key, preserving order. Used by the UI
 * to render the top-level Imported / Excluded / Failed / Removed bands.
 */
export function groupCategoriesByKind(
  categories: CategoryDescriptor[],
): Record<'imported' | 'excluded' | 'failed' | 'removed', CategoryDescriptor[]> {
  const out = { imported: [] as CategoryDescriptor[], excluded: [] as CategoryDescriptor[], failed: [] as CategoryDescriptor[], removed: [] as CategoryDescriptor[] };
  for (const c of categories) out[c.group].push(c);
  return out;
}

/**
 * Sum counts for a group — the header pill shows this number.
 */
export function groupCount(categories: CategoryDescriptor[]): number {
  let n = 0;
  for (const c of categories) n += c.count;
  return n;
}
